import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TopActionBar from '../../uikit/top_actionbar';
import TransactionDetailsModal from './TransactionDetailsModal';
import {
  getINRLocaleStringWithCurrency,
  getDateModifiedFormat,
  getDateModifiedFormat2,
} from '../../utils/commonUtils';
import DateSelector from './DateSelector';
import { useQuery } from '@tanstack/react-query';
import {
  getIncomingPayments,
  getOutgoingPayments,
  getProjectIDList,
} from '../../utils/apiUtils/common';

const SampleIconDown = () => (
  <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-bold">
    ↓
  </div>
);
const SampleIconUp = () => (
  <div className="w-8 h-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center font-bold">
    ↑
  </div>
);

const SummaryCard = ({ title, amount, sublabels, variant = 'inflow' }) => {
  const bg =
    variant === 'inflow'
      ? 'bg-green-50 border-green-100'
      : 'bg-red-50 border-red-100';
  const text = variant === 'inflow' ? 'text-green-700' : 'text-red-700';
  const Icon = variant === 'inflow' ? SampleIconDown : SampleIconUp;

  return (
    <div className={`rounded-xl border p-5 ${bg} shadow-sm`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-gray-600">{title}</div>
          <div className={`mt-2 text-2xl font-semibold ${text}`}>
            {getINRLocaleStringWithCurrency(amount)}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Icon />
        </div>
      </div>

      {sublabels && (
        <div className="mt-4 grid grid-cols-3 gap-4 text-sm text-gray-600">
          {sublabels.map((s, i) => (
            <div key={i} className="flex flex-col">
              <span className="text-xs">{s.label}</span>
              <span className="font-medium mt-1 text-gray-900">
                {getINRLocaleStringWithCurrency(s.value)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

SummaryCard.propTypes = {
  title: PropTypes.string.isRequired,
  amount: PropTypes.number,
  sublabels: PropTypes.array,
  variant: PropTypes.oneOf(['inflow', 'outflow']),
};

const RightGroup = ({
  title,
  items = [],
  color,
  setSelectedItem,
  setShowModal,
}) => {
  return (
    <div className="bg-white overflow-hidden">
      <div
        className={`px-5 pt-3 pb-1 border-y bg-${color}-50 text-sm font-semibold text-gray-700`}
      >
        {title}
      </div>

      <div className="p-4 space-y-3 max-h-[42vh] overflow-auto">
        {items.length === 0 ? (
          <div className="text-sm text-gray-500">No items</div>
        ) : (
          items.map((it, idx) => (
            <div
              key={idx}
              className="flex items-start justify-between cursor-pointer"
              onClick={() => {
                setSelectedItem(it);
                setShowModal(true);
              }}
            >
              {/* LEFT: TITLE + SUBTITLE */}
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {it.title}
                </div>
                <div className="text-xs text-gray-500">{it.subtitle}</div>
              </div>

              {/* RIGHT: AMOUNT + ARROW */}
              <div
                className={`flex items-center gap-2 text-sm font-semibold text-${color}-600 ml-4`}
              >
                {getINRLocaleStringWithCurrency(it.amount || 0, 0)}
                <span className="text-gray-400 text-lg leading-none">›</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

RightGroup.propTypes = {
  title: PropTypes.string,
  items: PropTypes.array,
  color: PropTypes.string,
  setShowModal: PropTypes.func,
  setSelectedItem: PropTypes.func,
};

const LeftGroup = ({
  title,
  items = [],
  color,
  setSelectedItem,
  setShowModal,
}) => {
  return (
    <div className="bg-white overflow-hidden">
      <div
        className={`px-5 pt-3 pb-1 border-y bg-${color}-50 text-sm font-semibold text-gray-700`}
      >
        {title}
      </div>

      <div className="p-4 space-y-3 max-h-[85vh] overflow-auto ">
        {items.length === 0 ? (
          <div className="text-sm text-gray-500">No items</div>
        ) : (
          items.map((it, idx) => (
            <div
              key={idx}
              className="flex items-start justify-between cursor-pointer"
              onClick={() => {
                setSelectedItem(it);
                setShowModal(true);
              }}
            >
              {/* LEFT: TITLE + SUBTITLE */}
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {it.title}
                </div>
                <div className="text-xs text-gray-500">{it.subtitle}</div>
              </div>

              {/* RIGHT: AMOUNT + ARROW */}
              <div
                className={`flex items-center gap-2 text-sm font-semibold text-${color}-600 ml-4`}
              >
                {getINRLocaleStringWithCurrency(it.amount || 0, 0)}
                <span className="text-gray-400 text-lg leading-none">›</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

LeftGroup.propTypes = {
  title: PropTypes.string,
  items: PropTypes.array,
  color: PropTypes.string,
  setShowModal: PropTypes.func,
  setSelectedItem: PropTypes.func,
};

const FinanceOverview = ({
  payments,
  outgoingPayments,
  rightGroups,
  leftGroups,
  setShowModal,
  setSelectedItem,
  setType,
}) => {
  return (
    <div className="w-full p-3 bg-white rounded-lg">
      {/* Top cards */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ">
        <SummaryCard
          title="Total Incoming"
          amount={
            payments?.incomingGrandTotal + payments?.incomingAdditionalAmt || 0
          }
          variant="inflow"
          sublabels={[
            {
              label: 'Total Payments',
              value: payments?.incomingGrandTotal || 0,
            },
            {
              label: 'Total Additional Payments',
              value: payments?.incomingAdditionalAmt || 0,
            },
          ]}
        />
        <SummaryCard
          title="Total Outgoing"
          amount={
            payments?.labourTotalPaid +
              payments?.labourAdditionalAmt +
              outgoingPayments?.finalOutgoingAmount || 0
          }
          variant="outflow"
          sublabels={[
            {
              label: 'PO Bills',
              value: outgoingPayments?.finalOutgoingAmount || 0,
            },
            { label: 'Labor Payments', value: payments?.labourTotalPaid || 0 },
            {
              label: 'Labor Additional Payments',
              value: payments?.labourAdditionalAmt || 0,
            },
          ]}
        />
      </div>

      {/* Details */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* LEFT: grouped lists (stacked small cards) */}
        <div>
          {leftGroups?.map((g, idx) => (
            <LeftGroup
              key={idx}
              title={g.title}
              items={g.items}
              color={'green'}
              setShowModal={setShowModal}
              setSelectedItem={setSelectedItem}
              setType={setType}
            />
          ))}
        </div>

        {/* RIGHT: transactions feed — map over your array */}
        <div>
          {rightGroups?.map((g, idx) => (
            <RightGroup
              key={idx}
              title={g.title}
              items={g.items}
              color={'red'}
              setShowModal={setShowModal}
              setSelectedItem={setSelectedItem}
              setType={setType}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

FinanceOverview.propTypes = {
  payments: PropTypes.object,
  outgoingPayments: PropTypes.object,
  leftGroups: PropTypes.array,
  rightGroups: PropTypes.array,
  transactions: PropTypes.array,
  headerImage: PropTypes.string,
  setShowModal: PropTypes.func,
  setSelectedItem: PropTypes.func,
  setType: PropTypes.func,
};

// ---------- SAMPLE DATA & EXPORT ----------

// export default example wrapper so you can paste directly
export default function FinanceOverviewPage() {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [opts, setOpts] = useState({
    fromDate: getDateModifiedFormat2(monthStart),
    toDate: getDateModifiedFormat2(today),
  });

  const { data: payments } = useQuery({
    queryKey: ['payments', opts],
    queryFn: () => getIncomingPayments(opts),
  });

  const { data: outgoingPayments } = useQuery({
    queryKey: ['outgoingPayments', opts],
    queryFn: () => getOutgoingPayments(opts),
  });

  const { data: projectList } = useQuery({
    queryKey: ['projectList'],
    queryFn: () => getProjectIDList(),
  });

  const getProjectName = (id) =>
    projectList?.find((p) => p.id === id)?.projectName || '-';

  const sampleLeftGroups = [
    {
      title: 'Installments',
      items: payments?.incomingPayments?.map((item) => ({
        title: `Installment for ${item?.projectName || '-'}`,
        subtitle: `${item?.paymentTransactionTime || '-'} • ${item?.paymentTransactionNo || '-'}`,
        amount: item?.Amount ?? 0,
        type: 'partpayment',
        id: item.id,
        projectName: item?.projectName,
        inflow: true,
      })),
    },
  ];

  const sampleRightGroups = [
    {
      title: 'Labour payments',
      items: payments?.labourPayments?.map((item) => ({
        title: `Labour payment for ${item?.labourName || '-'} `,
        subtitle: ` Project :${item?.ProjectName || '-'} • ${item?.paymentTransactionTime || '-'}`,
        amount: item?.totalAmount ?? 0,
        type: 'labour',
        id: item.id,
        projectName: item?.ProjectName,
        inflow: false,
      })),
    },
    {
      title: 'Po Bills',
      items: outgoingPayments?.poBills?.map((item) => ({
        title: `Po Bill Number ${item?.workOrderId || '-'}`,
        subtitle: ` Project :${getProjectName(item?.projectId)} • ${getDateModifiedFormat(item?.createdDate) || '-'} `,
        amount: item?.totalAmount ?? 0, //
        type: 'po',
        id: item.id,
        projectName: getProjectName(item?.projectId),
        inflow: false,
      })),
    },
  ];

  return (
    <>
      <TopActionBar
        containerClassName="px-4"
        leftContent={
          <div className="flex items-center justify-center gap-4">
            <h2 className="p-2 font-semibold md:p-0 text-white">
              <span>Account Details</span>
            </h2>
          </div>
        }
        rightContent={
          <DateSelector
            dateFormat2={true}
            onDateChange={(from, to) => {
              setOpts({
                fromDate: from,
                toDate: to,
              });
            }}
          />
        }
      />
      <FinanceOverview
        payments={payments}
        outgoingPayments={outgoingPayments}
        leftGroups={sampleLeftGroups}
        rightGroups={sampleRightGroups}
        setShowModal={setShowModal}
        setSelectedItem={setSelectedItem}
      />
      <TransactionDetailsModal
        open={showModal}
        onClose={() => setShowModal(false)}
        selectedItem={selectedItem}
      />
    </>
  );
}
